import React, { useEffect, useMemo, useState } from 'react';
import {
	BrowserRouter as Router,
	Routes,
	Route,
	useNavigate,
	Navigate,
	useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoginScreen from './LoginScreen';
import { AppDispatch, RootState } from '../data/store';
import ProjectsRouter from './projects/ProjectsRouter';
import HomeDashboardScreen from './dashboard/HomeDashboardScreen';
import ClientsRouter from './clients/ClientsRouter';
import AlertBanner from './common/AlertBanner';
import OAuth2CallbackScreen from './OAuth2CallbackScreen';
import MiniDrawer from './navigation/Drawer';
import SettingsScreen from './settings/SettingsScreen';
import CompanyScreen from './company/CompanyScreen';
import ProfileScreen from './profile/ProfileScreen';
import PolicyScreen from './policy/PolicyScreen';
import HelpScreen from './help/HelpScreen';
import { fetchSelf } from '../data/thunks/userThunk';
import ProjectCreationModal from './projects/creation/ProjectCreationModal';
import ClientCreationModal from './clients/creation/ClientCreationModal';
import AddUsersModal from './crew/AddCrewModal';
import TemplatesScreen from './templates/TemplatesScreen';
import UserAgreementModal from './UserAgreementModal';
import SubscriptionModal from './SubscriptionModal';
import PrivacyPolicy from './PrivacyPolicy';
import TermsAndConditions from './TermsAndConditions';
import SubscriptionComplete from './subscription/SubscriptionComplete';
import SignupScreen from './SignupScreen';
import SignupCompanyScreen from './SignupCompanyScreen';
import { CompanyStatuses } from '../data/models/company';
import TourModal from './TourModal';
import GoogleWorkspaceSetupModal from './GoogleWorkspaceSetupModal';
import SignupSubscriptionModal from './SignupSubscriptionModal';
import ProjectConfigurationRouter from './project-configuration/ProjectConfigurationRouter';
import {
	ProjectTypesRedirect,
	ProjectSubTypesRedirect,
	ProjectSettingsRedirect,
} from './project-configuration/LegacyRedirects';
import CrewScreen from './crew/CrewScreen';
import OnboardingScreen from './crew/onboarding/external/OnboardingScreen';
import InternalOnboardingScreen from './crew/onboarding/internal/InternalOnboardingScreen';
import { fetchCompany } from '../data/thunks/companyThunk';
import { UserStatus } from '../data/models/user';
import AddGroupModal from './crew/AddGroupModal';
import BusySpinner from './common/BusySpinner';
import { refreshAccessToken } from '../clients/axios-client';
import { setAstroToken } from '../data/stores/authStore';

function AuthRoutes() {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const location = useLocation();
	const [authBootstrapped, setAuthBootstrapped] = useState(false);
	const { astroToken, user } = useSelector(
		(state: RootState) => state.authState
	);

	const {
		company: { status: companyStatus },
	} = useSelector((state: RootState) => state.companyState);

	const isPublicRoute = useMemo(() => {
		return [
			'/login',
			'/oauth2callback',
			'/privacy-policy',
			'/terms-and-conditions',
			'/signup',
			'/signup-company',
			'/onboarding',
			'/internal-onboarding',
		].includes(location.pathname);
	}, [location.pathname]);

	// Bootstrap auth on initial load by attempting a silent refresh using the
	// HTTP-only refresh cookie. This keeps users logged in across reloads while
	// keeping the access token in memory only.
	useEffect(() => {
		let cancelled = false;
		const bootstrap = async () => {
			// If we already have a token, nothing to do.
			if (astroToken) {
				if (!cancelled) setAuthBootstrapped(true);
				return;
			}
			try {
				const newToken = await refreshAccessToken();
				if (!cancelled && newToken) {
					dispatch(setAstroToken(newToken));
				}
			} catch (e) {
				// No valid refresh cookie/session; continue unauthenticated.
			} finally {
				if (!cancelled) setAuthBootstrapped(true);
			}
		};

		// Only run once until bootstrapped; if user logs out later, routing handles it.
		if (!authBootstrapped) bootstrap();
		return () => {
			cancelled = true;
		};
	}, [authBootstrapped, astroToken, dispatch]);

	useEffect(() => {
		// Don't redirect while we are still attempting silent refresh.
		if (!authBootstrapped) return;
		if (!astroToken || !user) {
			if (
				location.pathname !== '/oauth2callback' &&
				location.pathname !== '/privacy-policy' &&
				location.pathname !== '/terms-and-conditions' &&
				location.pathname !== '/signup' &&
				location.pathname !== '/onboarding'
			) {
				navigate('/login');
			}
		} else if (
			companyStatus === CompanyStatuses.INCOMPLETE.id &&
			location.pathname !== '/signup-company'
		) {
			navigate('/signup-company');
		} else if (
			user?.status === UserStatus.UNSUBSCRIBED.id &&
			location.pathname !== '/internal-onboarding'
		) {
			// Redirect UNSUBSCRIBED users to internal onboarding
			navigate('/internal-onboarding');
		}
	}, [
		authBootstrapped,
		astroToken,
		user,
		navigate,
		location.pathname,
		companyStatus,
	]);

	useEffect(() => {
		if (astroToken) {
			dispatch(fetchSelf());
			dispatch(fetchCompany());
		}
	}, [astroToken, dispatch]);

	const showMiniDrawer = ![
		'/login',
		'/oauth2callback',
		'/privacy-policy',
		'/terms-and-conditions',
		'/signup',
		'/signup-company',
		'/onboarding',
		'/internal-onboarding',
	].includes(location.pathname);

	if (!authBootstrapped && !isPublicRoute) {
		return <BusySpinner display="FULL_SCREEN" />;
	}

	return (
		<main>
			<UserAgreementModal />
			<GoogleWorkspaceSetupModal />
			<TourModal />
			<ProjectCreationModal />
			<ClientCreationModal />
			<SubscriptionModal />
			<SignupSubscriptionModal />
			<AddUsersModal />
			<AddGroupModal />
			{showMiniDrawer ? (
				<MiniDrawer>
					<Routes>
						<Route path="/" element={<HomeDashboardScreen />} />
						<Route
							path="/projects/*"
							element={<ProjectsRouter />}
						/>
						<Route
							path="/templates"
							element={
								<Navigate to="/templates/default" replace />
							}
						/>
						<Route
							path="/templates/*"
							element={<TemplatesScreen />}
						/>
						<Route path="/clients/*" element={<ClientsRouter />} />
						<Route
							path="/crew"
							element={<Navigate to="/crew/clients" replace />}
						/>
						<Route path="/crew/*" element={<CrewScreen />} />
						<Route
							path="/project-configuration"
							element={
								<Navigate
									to="/project-configuration/types"
									replace
								/>
							}
						/>
						<Route
							path="/project-configuration/*"
							element={<ProjectConfigurationRouter />}
						/>
						{/* Legacy routes - redirect to new consolidated route */}
						<Route
							path="/project-types"
							element={<ProjectTypesRedirect />}
						/>
						<Route
							path="/project-types/:configurationId"
							element={<ProjectTypesRedirect />}
						/>
						<Route
							path="/project-sub-types"
							element={<ProjectSubTypesRedirect />}
						/>
						<Route
							path="/project-sub-types/:projectSubTypeId"
							element={<ProjectSubTypesRedirect />}
						/>
						<Route
							path="/project-settings/data"
							element={<ProjectSettingsRedirect />}
						/>
						<Route
							path="/project-settings/data/:configurationId"
							element={<ProjectSettingsRedirect />}
						/>
						<Route path="/settings" element={<SettingsScreen />} />
						<Route path="/company" element={<CompanyScreen />} />
						<Route path="/profile" element={<ProfileScreen />} />
						<Route path="/policy" element={<PolicyScreen />} />
						<Route path="/help" element={<HelpScreen />} />
						<Route
							path="/subscription/complete"
							element={<SubscriptionComplete />}
						/>
						<Route path="*" element={<Navigate to="/" replace />} />
					</Routes>
				</MiniDrawer>
			) : (
				<Routes>
					<Route path="/login" element={<LoginScreen />} />
					<Route path="/signup" element={<SignupScreen />} />
					<Route
						path="/signup-company"
						element={<SignupCompanyScreen />}
					/>
					<Route
						path="/oauth2callback"
						element={<OAuth2CallbackScreen />}
					/>
					<Route path="/privacy-policy" element={<PrivacyPolicy />} />
					<Route
						path="/terms-and-conditions"
						element={<TermsAndConditions />}
					/>
					<Route path="/onboarding" element={<OnboardingScreen />} />
					<Route
						path="/internal-onboarding"
						element={<InternalOnboardingScreen />}
					/>
					<Route path="*" element={<Navigate to="/" replace />} />
				</Routes>
			)}
			<AlertBanner />
		</main>
	);
}

export default function AuthenticatedRouter() {
	return (
		<Router>
			<AuthRoutes />
		</Router>
	);
}
